import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

interface Props {
  images: string[];
  alt: string;
  className?: string;
  intervalMs?: number;
}

export function PopupImageCarousel({ images, alt, className, intervalMs = 4000 }: Props) {
  const [failed, setFailed] = useState<string[]>([]);
  const usable = useMemo(
    () => images.filter((src) => !failed.includes(src)),
    [images, failed],
  );
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchX = useRef<number | null>(null);

  const count = usable.length;

  useEffect(() => {
    if (idx >= count && count > 0) setIdx(0);
  }, [count, idx]);

  useEffect(() => {
    if (count < 2 || paused) return;
    const t = window.setInterval(
      () => setIdx((i) => (i + 1) % count),
      intervalMs,
    );
    return () => window.clearInterval(t);
  }, [count, paused, intervalMs]);

  // Preload next image
  useEffect(() => {
    if (count < 2) return;
    const next = usable[(idx + 1) % count];
    if (!next) return;
    const img = new Image();
    img.referrerPolicy = "no-referrer";
    img.src = next;
  }, [idx, count, usable]);

  if (count === 0) return null;

  return (
    <div
      className={cn("relative overflow-hidden rounded-xl bg-muted", className)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={(e) => {
        touchX.current = e.touches[0]?.clientX ?? null;
      }}
      onTouchEnd={(e) => {
        const start = touchX.current;
        touchX.current = null;
        if (start === null || count < 2) return;
        const dx = (e.changedTouches[0]?.clientX ?? start) - start;
        if (Math.abs(dx) < 40) return;
        setIdx((i) => (dx < 0 ? (i + 1) % count : (i - 1 + count) % count));
      }}
    >
      {usable.map((src, i) => (
        <img
          key={src}
          src={src}
          alt={alt}
          loading={i === 0 ? "eager" : "lazy"}
          decoding="async"
          referrerPolicy="no-referrer"
          onError={() => setFailed((f) => (f.includes(src) ? f : [...f, src]))}
          className={cn(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-700",
            i === idx ? "opacity-100" : "opacity-0",
          )}
        />
      ))}
      {count > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {usable.map((src, i) => (
            <button
              key={src}
              type="button"
              aria-label={`Show image ${i + 1}`}
              onClick={(e) => {
                e.stopPropagation();
                setIdx(i);
              }}
              className={cn(
                "h-2 rounded-full transition-all",
                i === idx ? "bg-white w-4" : "bg-white/60 hover:bg-white/80 w-2",
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
